import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError, fmtINR, fmtDate } from "../lib/apiClient";
import { DOC_TYPES, DOC_TYPE_BY_PATH, statusClass, DOC_STATUS_OPTIONS } from "../lib/constants";
import PageHeader from "../components/PageHeader";
import { Printer, DownloadSimple, ArrowLeft, PencilSimple, Trash, ArrowsClockwise, Plus, HandCoins, X, Receipt, WhatsappLogo, Share, CheckCircle, Gauge, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { FieldStyle } from "./PartiesPage";
import ConvertToInvoiceModal from "../components/ConvertToInvoiceModal";
import GenerateGatePassModal from "../components/GenerateGatePassModal";
import TallyInvoicePrint from "../components/TallyInvoicePrint";
import { PdfPreviewModal } from "../components/PdfPreviewModal";

// Convert number to Indian words (approx, supports up to crores)
function numToIndianWords(num) {
  if (num === 0) return "Zero";
  const a = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const b = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  function inWords(n) {
    if (n < 20) return a[n];
    if (n < 100) return b[Math.floor(n/10)] + (n%10 ? ' ' + a[n%10] : '');
    if (n < 1000) return a[Math.floor(n/100)] + ' Hundred' + (n%100 ? ' ' + inWords(n%100) : '');
    return '';
  }
  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);
  let str = '';
  const crore = Math.floor(rupees / 10000000);
  const lakh = Math.floor((rupees % 10000000) / 100000);
  const thousand = Math.floor((rupees % 100000) / 1000);
  const rest = rupees % 1000;
  if (crore) str += inWords(crore) + ' Crore ';
  if (lakh) str += inWords(lakh) + ' Lakh ';
  if (thousand) str += inWords(thousand) + ' Thousand ';
  if (rest) str += inWords(rest);
  str = str.trim() + ' Rupees';
  if (paise) str += ' and ' + inWords(paise) + ' Paise';
  return str + ' Only';
}

export default function DocumentViewPage() {
  const { docPath, id } = useParams();
  const docType = DOC_TYPE_BY_PATH[docPath];
  const cfg = DOC_TYPES[docType];
  const nav = useNavigate();
  const [doc, setDoc] = useState(null);
  const [company, setCompany] = useState(null);
  const [payments, setPayments] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showGatePassModal, setShowGatePassModal] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);
  const closePdf = useCallback(() => setPdfPreview(null), []);

  const loadDoc = useCallback(async () => {
    const [d, c] = await Promise.all([api.get(`/documents/${id}`), api.get("/company")]);
    setDoc(d.data);
    setCompany(c.data || {});
    if (cfg?.showPayments) {
      const p = await api.get("/payments", { params: { invoice_id: id } });
      setPayments(p.data);
    }
  }, [id, cfg]);

  useEffect(() => {
    loadDoc().catch(e => toast.error(formatApiError(e?.response?.data?.detail)));
  }, [loadDoc]);

  if (!doc || !company) return <div className="p-8 text-ink-muted">Loading…</div>;

  const party = doc.party_snapshot || {};
  const print = () => window.print();

  const downloadEway = async () => {
    try {
      const { data } = await api.get(`/documents/${id}/ewaybill`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `eway-${doc.doc_number}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("E-Way Bill JSON downloaded");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const changeStatus = async (status) => {
    if (status === doc.status) return;
    let body = { status };
    if (status === "cancelled") {
      const reason = window.prompt("Cancellation reason (required) — this will be stored on the document and printed:");
      if (!reason || !reason.trim()) {
        toast.error("Cancellation reason is required");
        return;
      }
      body.cancellation_reason = reason.trim();
    }
    try {
      const { data } = await api.patch(`/documents/${id}/status`, body);
      setDoc(data);
      toast.success("Status updated");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const markCompleted = async () => {
    if (!window.confirm(`Mark this ${cfg.label} as Completed?`)) return;
    await changeStatus("completed");
  };

  const hasActiveInvoice = (doc.linked_doc_ids || []).length > 0 && (doc.linked_target_types || []).includes("invoice");
  const hasActiveGatePass = (doc.linked_doc_ids || []).length > 0 && (doc.linked_target_types || []).includes("gate_pass");

  const convert = async (target) => {
    try {
      const { data } = await api.post(`/documents/${id}/convert`, { target });
      toast.success(`Converted to ${DOC_TYPES[target].label}`);
      nav(`/${DOC_TYPES[target].path}/${data.id}`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete this ${cfg.label}?`)) return;
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); nav(`/${cfg.path}`); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const shareWhatsApp = async () => {
    const party = doc.party_snapshot || {};
    const digits = String(party.phone || "").replace(/[^0-9]/g, "");
    const phone = digits ? (digits.length === 10 ? `91${digits}` : digits) : "";
    const text = `Namaste ${party.name || "Customer"},\n\nPlease find your ${cfg.label} *${doc.doc_number}* from Ramkishan Agri Innovate.\n\n*Amount:* ₹${Number(doc.grand_total || 0).toFixed(2)}\n*Date:* ${doc.doc_date}`;

    // 1. Try the backend Gallabox push first — sends actual WhatsApp message with PDF attached
    try {
      const { data: gb } = await api.post(`/documents/${id}/whatsapp/send`, {});
      toast.success(`WhatsApp sent to ${gb.phone}${gb.message_id ? ` (id: ${gb.message_id})` : ""}`);
      return;
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 503) {
        // Gallabox not configured / no template — fall through to simple link share
      } else if (status === 400) {
        toast.error(typeof detail === "string" ? detail : "Customer phone is missing");
        return;
      } else if (status === 502) {
        const gbErr = detail?.gallabox_error;
        toast.error(`Gallabox rejected the message: ${typeof gbErr === "string" ? gbErr : JSON.stringify(gbErr)}`);
        return;
      } else if (status) {
        toast.error(formatApiError(detail) || e.message);
        return;
      }
      // Network error → fall through to simple link share
    }

    // 2. Fallback (until Gallabox template is approved): generate a public share link with ?dl=1
    //    so recipient's tap auto-downloads the PDF, then open WhatsApp Web with the message + link.
    try {
      const { data } = await api.post(`/documents/${id}/share`);
      const pdfUrl = `${data.share_url}${data.share_url.includes("?") ? "&" : "?"}dl=1`;
      const linkText = `${text}\n\n*Tap to view / download PDF:* ${pdfUrl}\n\nFor any queries please reply to this message. Thank you!`;
      const waUrl = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(linkText)}` : `https://wa.me/?text=${encodeURIComponent(linkText)}`;
      window.open(waUrl, "_blank");
      toast.success("Share link ready — WhatsApp opened");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const downloadPdf = () => {
    const fname = `${cfg.label.replace(/\s+/g, "_")}_${doc.doc_number}.pdf`;
    // Server-rendered PDF (Playwright + Chromium) so the file is byte-identical for everyone:
    // in-app preview here, WhatsApp recipient via Gallabox, and any link share.
    const src = async () => {
      const { data: share } = await api.post(`/documents/${id}/share`);
      return fetch(`${share.share_url.replace(/\/public\/invoice\//, "/api/public/invoice/")}/pdf`);
    };
    setPdfPreview({ src, filename: fname, title: `${cfg.label} · ${doc.doc_number}` });
  };

  const copyShareLink = async () => {
    try {
      const { data } = await api.post(`/documents/${id}/share`);
      await navigator.clipboard.writeText(data.share_url);
      toast.success("Share link copied to clipboard");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid={`doc-view-${docType}`}>
      {pdfPreview && <PdfPreviewModal src={pdfPreview.src} filename={pdfPreview.filename} title={pdfPreview.title} onClose={closePdf} />}
      <div className="no-print">
        <PageHeader
          title={`${cfg.label} · ${doc.doc_number}`}
          subtitle={<span className={`status-pill ${statusClass(doc.status)}`}>{doc.status}</span>}
          actions={
            <>
              <Link to={`/${cfg.path}`} className="btn-secondary inline-flex items-center gap-1" data-testid="back-btn"><ArrowLeft size={14} /> Back</Link>
              <Link to={`/${cfg.path}/${id}/edit`} className="btn-secondary inline-flex items-center gap-1" data-testid="edit-doc-btn"><PencilSimple size={14} /> Edit</Link>
              <select className="py-2 px-3 text-sm bg-white border border-input rounded-md outline-none" value={doc.status} onChange={e => changeStatus(e.target.value)} data-testid="change-status-select">
                {(DOC_STATUS_OPTIONS[docType] || ["draft","sent","accepted","rejected","cancelled","paid","dispatched"]).map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
                {/* keep the current value selectable even if not in the workflow set (legacy data) */}
                {!(DOC_STATUS_OPTIONS[docType] || ["draft","sent","accepted","rejected","cancelled","paid","dispatched"]).includes(doc.status) && (
                  <option value={doc.status}>{doc.status} (legacy)</option>
                )}
              </select>
              {/* Mark as completed (manual) for SO + Repair */}
              {(docType === "sales_order" || docType === "repair_invoice") && doc.status !== "completed" && doc.status !== "cancelled" && (
                <button onClick={markCompleted} className="btn-secondary inline-flex items-center gap-1" data-testid="mark-completed-btn"><CheckCircle size={14} weight="bold" /> Mark Completed</button>
              )}
              {docType === "quotation" && (
                <>
                  <button onClick={() => convert("sales_order")} className="btn-secondary inline-flex items-center gap-1" data-testid="convert-to-so"><ArrowsClockwise size={14} /> To Sales Order</button>
                  {!hasActiveInvoice && <button onClick={() => setShowConvertModal(true)} className="btn-secondary inline-flex items-center gap-1" data-testid="gen-invoice-btn"><Receipt size={14} weight="bold" /> Generate Tax Invoice</button>}
                </>
              )}
              {cfg.canConvertToInvoice && docType !== "quotation" && (
                hasActiveInvoice ? (
                  <span className="status-pill status-success inline-flex items-center gap-1" data-testid="invoice-already-linked"><CheckCircle size={12} weight="fill" /> Tax Invoice generated</span>
                ) : (
                  <button onClick={() => setShowConvertModal(true)} className="btn-secondary inline-flex items-center gap-1" data-testid="gen-invoice-btn"><Receipt size={14} weight="bold" /> Generate Tax Invoice</button>
                )
              )}
              {cfg.showEway && (
                <button onClick={downloadEway} className="btn-secondary inline-flex items-center gap-1" data-testid="download-eway"><DownloadSimple size={14} /> E-Way JSON</button>
              )}
              {/* Generate Gate Pass — for Tax Invoice / Repair Invoice / Delivery Challan / Job Work Challan */}
              {cfg.canConvertToGatePass && (
                hasActiveGatePass ? (
                  <span className="status-pill status-success inline-flex items-center gap-1" data-testid="gate-pass-already-linked"><CheckCircle size={12} weight="fill" /> Gate Pass generated</span>
                ) : (
                  <button onClick={() => setShowGatePassModal(true)} className="btn-secondary inline-flex items-center gap-1" data-testid="gen-gate-pass-btn"><Gauge size={14} weight="bold" /> Generate Gate Pass</button>
                )
              )}
              {/* WhatsApp share + copy link for every doc type */}
              <button onClick={shareWhatsApp} className="btn-secondary inline-flex items-center gap-1 text-[#128C7E]" data-testid="share-whatsapp"><WhatsappLogo size={14} weight="fill" /> WhatsApp</button>
              <button onClick={downloadPdf} className="btn-secondary inline-flex items-center gap-1" data-testid="download-pdf"><FilePdf size={14} weight="bold" /> View PDF</button>
              <button onClick={copyShareLink} className="btn-secondary inline-flex items-center gap-1" data-testid="copy-share-link"><Share size={14} weight="bold" /> Copy Link</button>
              <button onClick={print} className="btn-primary inline-flex items-center gap-1" data-testid="print-doc"><Printer size={14} /> Print / PDF</button>
              <button onClick={remove} className="btn-ghost inline-flex items-center gap-1 text-destructive" data-testid="delete-doc-btn"><Trash size={14} /> Delete</button>
            </>
          }
        />
      </div>

      {/* Printable area */}
      <div className="p-6 max-w-[900px] mx-auto print-container">
        {doc.status === "cancelled" && (
          <div className="no-print mb-3 rounded-md border-2 border-destructive/40 bg-destructive/5 px-4 py-2.5 text-xs">
            <div className="font-display font-semibold text-destructive uppercase tracking-wider">Cancelled</div>
            {doc.cancellation_reason && <div className="text-ink mt-1"><span className="label-overline">Reason:</span> {doc.cancellation_reason}</div>}
            {doc.cancelled_at && <div className="text-ink-muted text-[10px] mt-0.5">Cancelled on {fmtDate(doc.cancelled_at)}</div>}
          </div>
        )}
        <TallyInvoicePrint doc={doc} company={company} party={party} cfg={cfg} docTypeLabel={cfg.label} />
        {/* Cancelled stamp on print */}
        {doc.status === "cancelled" && (
          <div className="cancelled-stamp print-only">CANCELLED</div>
        )}

        {/* Extra fields panel (non-print) */}
        {(doc.expected_delivery_date || doc.advance_amount > 0 || doc.serial_number || doc.purpose_of_job_work) && (
          <div className="no-print mt-5 card-base p-5 grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
            {doc.expected_delivery_date && <InfoItem k="Expected Delivery" v={fmtDate(doc.expected_delivery_date)} />}
            {doc.advance_amount > 0 && <InfoItem k="Advance Received" v={`${fmtINR(doc.advance_amount)} · ${doc.advance_mode || "—"}${doc.advance_reference ? ` · ${doc.advance_reference}` : ""}`} />}
            {doc.serial_number && <InfoItem k="Serial No." v={doc.serial_number} />}
            {doc.item_make_model && <InfoItem k="Item / Machine" v={doc.item_make_model} />}
            {doc.issue_description && <InfoItem k="Issue" v={doc.issue_description} full />}
            {doc.purpose_of_job_work && <InfoItem k="Purpose" v={doc.purpose_of_job_work} />}
            {doc.expected_return_date && <InfoItem k="Expected Return" v={fmtDate(doc.expected_return_date)} />}
            {doc.item_condition && <InfoItem k="Condition" v={doc.item_condition} />}
            {doc.doc_type === "job_work_challan" && <InfoItem k="Returnable" v={doc.is_returnable ? "Yes" : "No (non-returnable)"} />}
          </div>
        )}

        {/* Payments panel (non-print) */}
        {cfg.showPayments && (
          <div className="no-print mt-5 card-base">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div>
                <div className="font-display font-semibold flex items-center gap-2"><HandCoins size={18} weight="duotone" className="text-brand" /> Payments Ledger</div>
                <div className="text-xs text-ink-muted mt-0.5">Balance due: <span className="font-semibold tabular text-destructive">{fmtINR(Math.max(0, (doc.grand_total || 0) - (doc.total_paid || 0)))}</span> · Total paid: <span className="font-semibold tabular text-[#1C4F32]">{fmtINR(doc.total_paid || 0)}</span></div>
              </div>
              <button onClick={() => setShowPaymentModal(true)} className="btn-primary inline-flex items-center gap-1" data-testid="add-payment-btn"><Plus size={14} weight="bold" /> Record Payment</button>
            </div>
            <div className="table-wrapper border-0 rounded-none">
              <table className="table-base">
                <thead><tr><th>Date</th><th>Mode</th><th>Reference</th><th className="text-right">Amount</th><th>Notes</th><th></th></tr></thead>
                <tbody>
                  {payments.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-ink-muted">No payments recorded. Click <span className="font-semibold">Record Payment</span> to add one.</td></tr>}
                  {payments.map(p => (
                    <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                      <td>{fmtDate(p.payment_date)}</td>
                      <td><span className="status-pill status-info">{p.mode}</span></td>
                      <td className="font-mono text-xs">{p.reference || "—"}</td>
                      <td className="text-right font-semibold tabular">{fmtINR(p.amount)}</td>
                      <td className="text-xs text-ink-muted">{p.notes || "—"}</td>
                      <td className="text-right">
                        <button onClick={async () => {
                          if (!window.confirm("Delete this payment?")) return;
                          try { await api.delete(`/payments/${p.id}`); toast.success("Deleted"); await loadDoc(); }
                          catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
                        }} className="btn-ghost text-destructive" data-testid={`delete-payment-${p.id}`}><Trash size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showPaymentModal && (
        <PaymentModal
          invoice={doc}
          balance={Math.max(0, (doc.grand_total || 0) - (doc.total_paid || 0))}
          onClose={() => setShowPaymentModal(false)}
          onSaved={async () => { setShowPaymentModal(false); await loadDoc(); }}
        />
      )}
      {showConvertModal && (
        <ConvertToInvoiceModal
          source={doc}
          target="invoice"
          onClose={() => setShowConvertModal(false)}
          onConverted={(newDoc) => { setShowConvertModal(false); nav(`/invoices/${newDoc.id}`); }}
        />
      )}
      {showGatePassModal && (
        <GenerateGatePassModal
          source={doc}
          onClose={() => setShowGatePassModal(false)}
          onConverted={(newDoc) => { setShowGatePassModal(false); nav(`/gate-passes/${newDoc.id}`); }}
        />
      )}
      <FieldStyle />
    </div>
  );
}

function InfoItem({ k, v, full }) {
  return (
    <div className={full ? "sm:col-span-3" : ""}>
      <div className="label-overline mb-0.5">{k}</div>
      <div className="text-sm font-semibold text-ink">{v}</div>
    </div>
  );
}

function PaymentModal({ invoice, balance, onClose, onSaved }) {
  const [form, setForm] = useState({
    amount: balance > 0 ? balance : invoice.grand_total || 0,
    mode: "UPI",
    reference: "",
    payment_date: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter a positive amount");
    setSaving(true);
    try {
      await api.post("/payments", {
        invoice_id: invoice.id,
        amount: Number(form.amount),
        mode: form.mode,
        reference: form.reference,
        payment_date: form.payment_date,
        notes: form.notes,
      });
      toast.success("Payment recorded");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="payment-modal">
      <div className="bg-white rounded-lg border border-border max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <div className="font-display text-lg font-semibold">Record Payment</div>
            <div className="text-xs text-ink-muted">Against invoice {invoice.doc_number} · Balance <span className="font-semibold tabular text-destructive">{fmtINR(balance)}</span></div>
          </div>
          <button onClick={onClose} className="btn-ghost"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><div className="label-overline block mb-1.5">Amount (₹) *</div><input type="number" step="0.01" className="field text-right" data-testid="pm-amount" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></div>
            <div><div className="label-overline block mb-1.5">Date *</div><input type="date" className="field" data-testid="pm-date" value={form.payment_date} onChange={e => setForm({ ...form, payment_date: e.target.value })} /></div>
            <div><div className="label-overline block mb-1.5">Mode *</div>
              <select className="field" data-testid="pm-mode" value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}>
                <option>Cash</option><option>UPI</option><option>Bank</option><option>Cheque</option><option>Card</option><option>Other</option>
              </select>
            </div>
            <div><div className="label-overline block mb-1.5">Reference / Txn ID</div><input className="field" data-testid="pm-ref" value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} /></div>
          </div>
          <div><div className="label-overline block mb-1.5">Notes</div><textarea rows={2} className="field" data-testid="pm-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-border bg-surface-muted">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="pm-save">{saving ? "Saving…" : "Save Payment"}</button>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-ink-muted">{k}</span>
      <span className="tabular font-semibold">{v}</span>
    </div>
  );
}
